import {
  DeleteOutlined,
  DownOutlined,
  LoadingOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button, Input, message, Select } from 'antd';
import { useState } from 'react';
import { getErpSkus } from '../api';
import { useTranslate } from '@tolgee/react';
import ErpSku from './ErpSku';
import Empty from 'components/Empty';
import { useFormikContext } from 'formik';
import { FV } from './share';
import styled from '@emotion/styled';

function ErpItemList({ storeId }: { storeId: string | number }) {
  const { t } = useTranslate();

  const [list, setList] = useState([0]);

  const [fold, setFold] = useState(true);

  return (
    <Wrapper>
      <section>
        <ErpItem
          key={list[0]}
          index={0}
          storeId={storeId}
          onDelete={() => setList(list.slice(1))}
        />
        {list.length > 2 && (
          <FoldLine>
            <span />
            <Button type="link" onClick={() => setFold(!fold)}>
              {fold ? `${t('展开补发商品')}（${list.length - 2}）` : t('phrase.收起')}
              {fold ? <DownOutlined /> : <UpOutlined />}
            </Button>
            <span />
          </FoldLine>
        )}
        {list.length > 1 &&
          list.slice(1).map((key, i) => {
            const index = i + 1;
            return (
              <ErpItem
                key={key}
                index={index}
                hide={fold && index < list.length - 1}
                storeId={storeId}
                onDelete={() => setList((pre) => [...pre.slice(0, index), ...pre.slice(index + 1)])}
              />
            );
          })}
      </section>
      {list.length < 5 && (
        <Button
          ghost
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setList([...list, list[list.length - 1] + 1])}
        >
          {t('新增补发商品')}
        </Button>
      )}
    </Wrapper>
  );
}

function ErpItem({
  index,
  hide,
  storeId,
  onDelete,
}: {
  index: number;
  hide?: boolean;
  storeId: string | number;
  onDelete: () => void;
}) {
  const { t } = useTranslate();

  const [type, setType] = useState<'SKU_CODE' | 'SKU_NAME'>('SKU_CODE');

  const {
    values,
    values: { skus = [] },
    setValues,
  } = useFormikContext<FV>();

  const setSku = (sku?: Todo.ErpOrderSku) =>
    setValues({
      ...values,
      skus: [...skus.slice(0, index), sku, ...skus.slice(index + 1)] as any,
    });

  const { data, loading, runAsync } = useRequest(
    (keyword: string) => {
      return getErpSkus({
        searchType: 'ERP_ITEM',
        storeId,
        erpSearchType: type,
        keyword,
        page: 1,
        pageSize: 1,
      }).then((res) => {
        if (res.length) {
          const { skuId, skuName, imgUrl } = res[0];
          setSku({
            skuId,
            skuName,
            imgUrl,
            skuAmount: '0',
            skuQuantity: 1,
            subSkus: [],
          });
        } else {
          setSku(undefined);
        }
        return res;
      });
    },
    {
      manual: true,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  return (
    <Container style={hide ? { display: 'none' } : undefined}>
      {index > 0 && (
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => {
            setValues({
              ...values,
              skus: [...skus.slice(0, index), ...skus.slice(index + 1)],
            });
            onDelete();
          }}
        />
      )}
      <Input.Search
        allowClear
        placeholder={t('trade.请输入')}
        onSearch={(value) => {
          if (!loading && value.trim()) {
            runAsync(value);
          }
        }}
        addonBefore={
          <Select
            dropdownMatchSelectWidth={false}
            value={type}
            onChange={setType}
            options={[
              { value: 'SKU_CODE', label: t('ERP系统商品编码') },
              { value: 'SKU_NAME', label: t('ERP系统商品名称') },
            ]}
          />
        }
        enterButton={loading ? <Button icon={<LoadingOutlined />} /> : undefined}
      />
      {data?.length && skus[index] ? (
        <ErpSku index={index} erpItemSku={data[0]} />
      ) : (
        <section>
          <Empty />
        </section>
      )}
    </Container>
  );
}

export const Wrapper = styled.div`
  margin-top: 16px;
  > section {
    max-height: 500px;
    overflow: auto;
    margin-bottom: 16px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 18px 16px;
  margin-bottom: 16px;
  background: #f7f9fa;
  > button {
    margin-top: -12px;
    align-self: flex-end;
  }
  &:last-of-type {
    margin-bottom: 0;
  }
  > span {
    margin-bottom: 16px;
  }
  > section {
    height: 108px;
    margin-top: 16px;
    background: #fff;
    .ant-empty-image {
      height: 80px;
      margin-bottom: 0;
      > img {
        height: 80px;
      }
    }
  }
`;

export const FoldLine = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  > span {
    flex: 1 0 0;
    height: 1px;
    background: #e9ebed;
  }
`;

export default ErpItemList;
